import React, { useEffect, useState } from "react";
import {
    Button,
    Grid,
    CircularProgress,
    InputLabel,
    makeStyles,
} from "@material-ui/core";
import { Form, FieldArray, Field, useFormik, FormikProvider } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { NODE_API_URL, BASE_URL } from "../../../../env_config";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { InputField } from "../CcmodeSetupNew/FormFields";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useCCmodeSettingContext } from "../../../../context/ccmodeSettingContext";
import { isEqual } from "lodash";
import UnSavedChangesPopUp from "../../Popup/UnSavedChangesPopUp";
import DOMPurify from "dompurify";

const validationSchemaInviteUser = Yup.object({
    users: Yup.array().of(
        Yup.object({
            customerId: Yup.string(),
            email: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            permissions: Yup.array()
                .of(Yup.number())
                .min(1, "At least one permission is required"),
            displayName: Yup.string().required("Display name is required"),
        })
    ),
});

const useStyles = makeStyles((theme) => ({
    formControl: {
        marginLeft: theme.spacing(1),
    },
    button: {
        marginLeft: theme.spacing(2),
    },
    wrapper: {
        margin: theme.spacing(1),
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
    },
    cross: {
        cursor: "pointer",
        width: "20px",
        height: "20px",
        position: "absolute",
        top: "0",
        right: "0",
        "&::before": {
            content: "''",
            position: "absolute",
            top: "50%",
            right: "0",
            width: "20px",
            height: "2px",
            backgroundColor: "rgba(0, 0, 0, 0.54)",
            transform: "translateY(-50%) rotate(45deg)",
        },
        "&::after": {
            content: "''",
            position: "absolute",
            top: "50%",
            right: "0",
            width: "20px",
            height: "2px",
            backgroundColor: "rgba(0, 0, 0, 0.54)",
            transform: "translateY(-50%) rotate(-45deg)",
        },
    },
    formControlRoot: {
        margin: 0,
        width: "calc(100% + 15px)",
        justifyContent: "space-between",
    },
    formControlRootLabel: {
        fontFamily: "Poppins, sans-serif !important",
        fontSize: "14px",
        fontWeight: "300",
        color: "#919191",
        textTransform: "capitalize",
        display: "flex",
        flexDirection: "column",
        marginBottom: "5px",
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "4px !important",
        },
        "& .MuiInputBase-input.Mui-disabled": {
            borderRadius: "4px",
            height: "0px",
        },
    },
}));

export default function CcmodeInviteUserForm({
    language,
    inviteUserObj,
    setInviteUserForm,
}) {
    const {
        activeKey,
        setIsFormChanged,
        showFormChangePopUp,
        setShowFormChangePopUp,
        nextKey,
        setNextKey,
        setActiveKey,
    } = useCCmodeSettingContext();

    const classes = useStyles();
    const [permissions, setPermissions] = useState(null);

    useEffect(() => {
        const source = axios.CancelToken.source();

        axios({
            method: "GET",
            url: `${NODE_API_URL}/INTL/fetch-ccmode-permissions`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("cust_auth_token")}`,
            },
            cancelToken: source.token,
        })
            .then((res) => {
                if (res.data.success) {
                    setPermissions(res.data.data);
                }
            })
            .catch((error) => {
                if (error?.response?.data?.message === "UnAuthorized!") {
                    var cookies = document.cookie.split(";");
                    for (var i = 0; i < cookies.length; i++) {
                        if (cookies[i] && !cookies[i].includes("view")) {
                            var equals = cookies[i].indexOf("=");
                            var name =
                                equals > -1
                                    ? cookies[i].substr(0, equals)
                                    : cookies[i];
                            document.cookie =
                                name +
                                "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                        }
                    }

                    window.location.replace(`${BASE_URL}/logout`);
                }
                if (error?.response?.data?.message) {
                    toast.error(error?.response?.data?.message);
                }
            });

        return () => {
            source.cancel("Component unmounted: cancel pending request");
        };
    }, []);

    function handleSubmit(values, actions) {
        if (!inviteUserObj.isUpdating) {
            axios({
                method: "POST",
                url: `${NODE_API_URL}/INTL/ccmode-invite/send`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Cookies.get("cust_auth_token")}`,
                },
                data: values,
            })
                .then((res) => {
                    if (res.data.success) {
                        toast.dismiss();
                        toast.success(res.data.message);
                        actions.resetForm();

                        setInviteUserForm((prev) => ({
                            ...prev,
                            isOpen: false,
                            isUpdating: false,
                        }));
                    }
                })
                .catch((error) => {
                    if (error?.response?.data?.message === "UnAuthorized!") {
                        var cookies = document.cookie.split(";");
                        for (var i = 0; i < cookies.length; i++) {
                            if (cookies[i] && !cookies[i].includes("view")) {
                                var equals = cookies[i].indexOf("=");
                                var name =
                                    equals > -1
                                        ? cookies[i].substr(0, equals)
                                        : cookies[i];
                                document.cookie =
                                    name +
                                    "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                            }
                        }

                        window.location.replace(`${BASE_URL}/logout`);
                    }
                    if (error?.response?.data?.message) {
                        toast.error(error?.response?.data?.message);
                    }
                })
                .finally(() => {
                    actions.setSubmitting(false);
                });
        } else {
            axios({
                method: "PATCH",
                url: `${NODE_API_URL}/INTL/update-ccmode-user/${inviteUserObj.userObj.id}`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Cookies.get("cust_auth_token")}`,
                },
                data: {
                    customer_id: values.users[0].customerId,
                    email: values.users[0].email,
                    display_name: values.users[0].displayName,
                    permissions: values.users[0].permissions,
                },
            })
                .then((res) => {
                    if (res.data.success) {
                        toast.dismiss();
                        toast.success(res.data.message);
                        actions.resetForm();

                        handlePopUpOnSuccess();

                        setInviteUserForm((prev) => ({
                            ...prev,
                            isOpen: false,
                            isUpdating: false,
                        }));
                    }
                })
                .catch((error) => {
                    if (error?.response?.data?.message === "UnAuthorized!") {
                        var cookies = document.cookie.split(";");
                        for (var i = 0; i < cookies.length; i++) {
                            if (cookies[i] && !cookies[i].includes("view")) {
                                var equals = cookies[i].indexOf("=");
                                var name =
                                    equals > -1
                                        ? cookies[i].substr(0, equals)
                                        : cookies[i];
                                document.cookie =
                                    name +
                                    "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                            }
                        }

                        window.location.replace(`${BASE_URL}/logout`);
                    }
                    if (error?.response?.data?.message) {
                        toast.error(error?.response?.data?.message);
                    }
                })
                .finally(() => {
                    actions.setSubmitting(false);
                });
        }
    }

    useEffect(() => {
        if (activeKey !== "ccmodeuserinvite") {
            setInviteUserForm((prev) => ({
                ...prev,
                isOpen: false,
                isUpdating: false,
            }));
        }
    }, [activeKey]);

    const formik = useFormik({
        initialValues: {
            users: [inviteUserObj.userObj],
        },
        enableReinitialize: true,
        validationSchema: validationSchemaInviteUser,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        if (inviteUserObj.isUpdating || inviteUserObj.isOpen) {
            const isMatched = isEqual(
                {
                    users: [inviteUserObj.userObj],
                },
                formik.values
            );

            if (!isMatched) {
                setIsFormChanged(true);
            } else {
                setIsFormChanged(false);
            }
        }
    }, [formik.values]);

    function handlePopUpOnSuccess() {
        setIsFormChanged(false);
        setShowFormChangePopUp(false);
        setNextKey(null);
    }

    return (
        <FormikProvider value={formik}>
            <Form>
                <FieldArray name="users">
                    {({ push, remove }) => (
                        <>
                            {formik.values.users.map((user, index) => (
                                <Grid
                                    key={index}
                                    container
                                    spacing={3}
                                    className={
                                        "border-bottom position-relative"
                                    }
                                >
                                    <Grid item xs={12} className="my-6">
                                        <Grid
                                            container
                                            spacing={3}
                                            alignItems="flex-start"
                                            justify="space-between"
                                        >
                                            <Grid item xs={12} md={4}>
                                                <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                                    {
                                                        language.CCMODE_INVITE_USER_DISPLAY_NAME
                                                    }
                                                    <span className="small d-block mt-2 line-height-lg">
                                                        display name
                                                    </span>
                                                </InputLabel>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Field
                                                    name={`users.${index}.displayName`}
                                                >
                                                    {({ field, meta }) => (
                                                        <InputField
                                                            {...field}
                                                            className={
                                                                classes.textField
                                                            }
                                                            fullWidth
                                                            InputProps={{
                                                                style: {
                                                                    height:
                                                                        "40px",
                                                                },
                                                            }}
                                                            error={
                                                                meta.touched &&
                                                                Boolean(
                                                                    meta.error
                                                                )
                                                            }
                                                            helperText={
                                                                meta.touched &&
                                                                meta.error
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <hr className="w-100" />
                                    <Grid item xs={12} className="my-6">
                                        <Grid
                                            container
                                            spacing={3}
                                            alignItems="flex-start"
                                            justify="space-between"
                                        >
                                            <Grid item xs={12} md={4}>
                                                <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                                    {
                                                        language.CCMODE_INVITE_USER_EMAIL
                                                    }
                                                    <span className="small d-block mt-2 line-height-lg">
                                                        email
                                                    </span>
                                                </InputLabel>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Field
                                                    name={`users.${index}.email`}
                                                >
                                                    {({ field, meta }) => (
                                                        <InputField
                                                            {...field}
                                                            className={
                                                                classes.textField
                                                            }
                                                            fullWidth
                                                            type="email"
                                                            InputProps={{
                                                                style: {
                                                                    height:
                                                                        "40px",
                                                                },
                                                            }}
                                                            disabled={
                                                                inviteUserObj
                                                                    .userObj
                                                                    .isDefaultUser
                                                            }
                                                            error={
                                                                meta.touched &&
                                                                Boolean(
                                                                    meta.error
                                                                )
                                                            }
                                                            helperText={
                                                                meta.touched &&
                                                                meta.error
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <hr className="w-100" />
                                    <Grid item xs={12} className="my-3">
                                        <Grid
                                            container
                                            spacing={3}
                                            alignItems="flex-start"
                                            justify="space-between"
                                        >
                                            <Grid item xs={6} md={4}>
                                                <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                                    User Permissions
                                                    <span className="small d-block mt-2 line-height-lg">
                                                        Grant access to key actions and features for CC Mode users.
                                                    </span>
                                                </InputLabel>
                                            </Grid>
                                            <Grid item>
                                                <legend
                                                    className="font-size-h6 font-weight-bold text-capitalize"
                                                    style={{
                                                        color: !!(
                                                            formik.errors
                                                                .users &&
                                                            formik.errors.users[
                                                                index
                                                            ] &&
                                                            formik.errors.users[
                                                                index
                                                            ].permissions &&
                                                            formik.touched
                                                                .users &&
                                                            formik.touched
                                                                .users[index] &&
                                                            formik.touched
                                                                .users[index]
                                                                .permissions
                                                        )
                                                            ? "#F64E63"
                                                            : "rgba(0, 0, 0, 0.54)",
                                                    }}
                                                >
                                                    Please select at least one permission*
                                                </legend>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {permissions
                                        ? permissions.map((permission, i) => {
                                              const isChecked = user.permissions.includes(
                                                  permission.id
                                              );
                                              const safeDescription = DOMPurify.sanitize(permission.description);

                                              return (
                                                  <React.Fragment key={i}>
                                                      <Grid item xs={12}>
                                                          <Grid
                                                              container
                                                              spacing={3}
                                                              alignItems="flex-start"
                                                              justify="space-between"
                                                          >
                                                              <Grid
                                                                  item
                                                                  xs={8}
                                                                  md={4}
                                                              >
                                                                  <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                                                      {
                                                                          permission.label
                                                                      }
                                                                      <span className="small d-block mt-2 line-height-lg" dangerouslySetInnerHTML={{ __html: safeDescription }} />
                                                                  </InputLabel>
                                                              </Grid>
                                                              <Grid item>
                                                                  {inviteUserObj
                                                                      .userObj
                                                                      .isDefaultUser ||
                                                                  !permission.is_active ? (
                                                                      <OverlayTrigger
                                                                          placement="top"
                                                                          overlay={
                                                                              <Tooltip id="tooltip">
                                                                                  {!permission.is_active
                                                                                      ? "Turned off by Administrator"
                                                                                      : inviteUserObj
                                                                                            .userObj
                                                                                            .isDefaultUser
                                                                                      ? "this permission is enabled for Default User. No manual changes are required."
                                                                                      : ""}
                                                                              </Tooltip>
                                                                          }
                                                                      >
                                                                          <label className="switch">
                                                                              <input
                                                                                  type="checkbox"
                                                                                  className="success"
                                                                                  checked={
                                                                                      isChecked &&
                                                                                      permission.is_active
                                                                                  }
                                                                                  name={`${permission.id}`}
                                                                                  disabled={
                                                                                      inviteUserObj
                                                                                          .userObj
                                                                                          .isDefaultUser ||
                                                                                      !permission.is_active
                                                                                  }
                                                                                  onChange={(
                                                                                      event
                                                                                  ) => {
                                                                                      const checked =
                                                                                          event
                                                                                              .target
                                                                                              .checked;
                                                                                      if (
                                                                                          checked
                                                                                      ) {
                                                                                          formik.setFieldValue(
                                                                                              `users.${index}.permissions`,
                                                                                              [
                                                                                                  ...user.permissions,
                                                                                                  permission.id,
                                                                                              ]
                                                                                          );
                                                                                      } else {
                                                                                          formik.setFieldValue(
                                                                                              `users.${index}.permissions`,
                                                                                              user.permissions.filter(
                                                                                                  (
                                                                                                      id
                                                                                                  ) =>
                                                                                                      id !==
                                                                                                      permission.id
                                                                                              )
                                                                                          );
                                                                                      }
                                                                                  }}
                                                                              />
                                                                              <span className="slider" />
                                                                          </label>
                                                                      </OverlayTrigger>
                                                                  ) : (
                                                                      <label className="switch">
                                                                          <input
                                                                              type="checkbox"
                                                                              className="success"
                                                                              checked={
                                                                                  isChecked &&
                                                                                  permission.is_active
                                                                              }
                                                                              name={`${permission.id}`}
                                                                              disabled={
                                                                                  inviteUserObj
                                                                                      .userObj
                                                                                      .isDefaultUser ||
                                                                                  !permission.is_active
                                                                              }
                                                                              onChange={(
                                                                                  event
                                                                              ) => {
                                                                                  const checked =
                                                                                      event
                                                                                          .target
                                                                                          .checked;
                                                                                  if (
                                                                                      checked
                                                                                  ) {
                                                                                      formik.setFieldValue(
                                                                                          `users.${index}.permissions`,
                                                                                          [
                                                                                              ...user.permissions,
                                                                                              permission.id,
                                                                                          ]
                                                                                      );
                                                                                  } else {
                                                                                      formik.setFieldValue(
                                                                                          `users.${index}.permissions`,
                                                                                          user.permissions.filter(
                                                                                              (
                                                                                                  id
                                                                                              ) =>
                                                                                                  id !==
                                                                                                  permission.id
                                                                                          )
                                                                                      );
                                                                                  }
                                                                              }}
                                                                          />
                                                                          <span className="slider" />
                                                                      </label>
                                                                  )}
                                                              </Grid>
                                                          </Grid>
                                                      </Grid>
                                                      {i === 1 || i === 4 ? (
                                                          <hr className="w-100" />
                                                      ) : (
                                                          ""
                                                      )}
                                                  </React.Fragment>
                                              );
                                          })
                                        : ""}
                                </Grid>
                            ))}
                            <Grid
                                className="bg-white sticky-top"
                                container
                                justify={"space-between"}
                                alignItems="center"
                                style={{ bottom: "0px", paddingBottom: "18px" }}
                            >
                                <Button
                                    onClick={() =>
                                        setInviteUserForm((prev) => ({
                                            ...prev,
                                            isOpen: false,
                                            isUpdating: false,
                                        }))
                                    }
                                    className={`btn btn-danger`}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={formik.isSubmitting}
                                    // type="submit"
                                    variant="contained"
                                    color="primary"
                                    className={`${classes.button} btn`}
                                    onClick={formik.submitForm}
                                >
                                    {inviteUserObj.isUpdating
                                        ? "Update User"
                                        : language.CCMODE_INVITE_USER_BUTTON}
                                    {formik.isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </Grid>
                        </>
                    )}
                </FieldArray>
            </Form>
            <UnSavedChangesPopUp
                show={showFormChangePopUp && activeKey === "ccmodeuserinvite"}
                onClose={() => {
                    formik.resetForm();
                    setIsFormChanged(false);
                    setShowFormChangePopUp(false);
                    if (nextKey) {
                        setActiveKey(nextKey);
                        setNextKey(null);
                    }
                    setInviteUserForm((prev) => ({
                        ...prev,
                        isOpen: false,
                        isUpdating: false,
                    }));
                }}
                onCross={() => {
                    setShowFormChangePopUp(false);
                }}
                message={`${
                    inviteUserObj.isUpdating
                        ? "you have unsaved changes in update user form"
                        : "you have unsaved changes in invite user form"
                }`}
                // saveBtn={
                //     inviteUserObj.isUpdating
                //         ? "continue updating"
                //         : "continue inviting"
                // }
                handleSave={() => {
                    if (inviteUserObj.isUpdating) {
                        setShowFormChangePopUp(false);
                    } else {
                        setShowFormChangePopUp(false);
                    }
                }}
            />
        </FormikProvider>
    );
}
